import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useState, useEffect, useRef, useCallback } from "react";
import SupportSessionBanner from "./components/SupportSessionBanner";

// iOS viewport height fix - sets CSS custom properties for accurate viewport height
// Uses requestAnimationFrame debouncing to prevent thrash during resize/orientation changes
const useIOSViewportFix = () => {
  const rafRef = useRef(null);
  
  const setViewportHeight = useCallback(() => {
    // Cancel any pending RAF
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    rafRef.current = requestAnimationFrame(() => {
      // Use visualViewport API for accurate height (especially when keyboard is open)
      const vh = window.visualViewport?.height || window.innerHeight;
      const vw = window.visualViewport?.width || window.innerWidth;
      
      // Set CSS custom properties
      const doc = document.documentElement;
      doc.style.setProperty('--app-vh', `${vh * 0.01}px`);
      doc.style.setProperty('--safe-vh', `${vh * 0.01}px`);
      
      // Modal height: leave 24px margin (12px top + 12px bottom)
      // This ensures modals fit within visible viewport even with keyboard open
      const modalHeight = Math.floor(vh - 24);
      doc.style.setProperty('--safe-modal-height', `${modalHeight}px`);
      
      // Modal width: leave 24px margin (12px left + 12px right), max 512px
      const modalWidth = Math.min(Math.floor(vw - 24), 512);
      doc.style.setProperty('--safe-modal-width', `${modalWidth}px`);
    });
  }, []);
  
  useEffect(() => {
    setViewportHeight();
    
    // Listen for viewport changes (critical for iOS keyboard)
    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener('resize', setViewportHeight);
      viewport.addEventListener('scroll', setViewportHeight);
    }
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', () => setTimeout(setViewportHeight, 100));
    
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (viewport) {
        viewport.removeEventListener('resize', setViewportHeight);
        viewport.removeEventListener('scroll', setViewportHeight);
      }
      window.removeEventListener('resize', setViewportHeight);
    };
  }, [setViewportHeight]);
};

// Contexts
import { EntitlementProvider } from "./contexts/EntitlementContext";
import { AuthProvider } from "./contexts/AuthContext";
import { FirstValueOnboardingProvider } from "./contexts/FirstValueOnboardingContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import Calendar from "./pages/Calendar";
import Estimates from "./pages/Estimates";
import CatalogManagement from "./pages/CatalogManagement";
import Invoices from "./pages/Invoices";
import Technicians from "./pages/Technicians";
import Settings from "./pages/Settings";
import Upgrades from "./pages/Upgrades";
import BillingPage from "./pages/BillingPage";
import SubscriptionManagement from "./pages/SubscriptionManagement";
import Support from "./pages/Support";
import Ideas from "./pages/Ideas";
import Pricing from "./pages/Pricing";
import ReviewSettings from "./pages/ReviewSettings";
import ReviewLanding from "./pages/ReviewLanding";
import ReviewAnalytics from "./pages/ReviewAnalytics";
import BookingSettings from "./pages/BookingSettings";
import PublicBookingPage from "./pages/PublicBookingPage";
import ReminderSettings from "./pages/ReminderSettings";
import EmailMarketing from "./pages/EmailMarketing";
import UnsubscribePage from "./pages/UnsubscribePage";
import BillingAnalytics from "./pages/BillingAnalytics";
import PublicEstimate from "./pages/PublicEstimate";
import PublicInvoice from "./pages/PublicInvoice";
import TrustCommerceReturnPage from "./pages/TrustCommerceReturnPage";
import ActivatePage from "./pages/ActivatePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Reports from "./pages/Reports";
import ReportsMoney from "./pages/ReportsMoney";
import ReportsWork from "./pages/ReportsWork";
import ReportsCustomers from "./pages/ReportsCustomers";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import FollowUps from "./pages/FollowUps";
import SupabaseAuthCallback from "./pages/SupabaseAuthCallback";
import AcceptInvite from "./pages/AcceptInvite";
import SetupPage from "./pages/SetupPage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminTenants from "./pages/admin/AdminTenants";
import AdminTenantLifecycle from "./pages/admin/AdminTenantLifecycle";
import AdminLaunchCleanup from "./pages/admin/AdminLaunchCleanup";
import AdminTickets from "./pages/admin/AdminTickets";
import AdminIdeas from "./pages/admin/AdminIdeas";
import AdminFeatures from "./pages/admin/AdminFeatures";
import AdminBundles from "./pages/admin/AdminBundles";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminAdmins from "./pages/admin/AdminAdmins";
import AdminSystemUtilities from "./pages/admin/AdminSystemUtilities";
import AdminSystemHealth from "./pages/admin/AdminSystemHealth";
import AdminBillingLogs from "./pages/admin/AdminBillingLogs";
import AdminServiceTypes from "./pages/admin/AdminServiceTypes";
import AdminTenantComms from "./pages/admin/AdminTenantComms";
import TemplateEditor from "./pages/admin/TemplateEditor";

// Tech Portal Pages
import TechDashboard from "./pages/tech/TechDashboard";
import TechVisits from "./pages/tech/TechVisits";
import TechVisitDetail from "./pages/tech/TechVisitDetail";

// Layouts
import AppLayout from "./layouts/AppLayout";
import AdminLayout from "./layouts/AdminLayout";
import TechLayout from "./layouts/TechLayout";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Auth context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);

  const checkAuth = async () => {
    try {
      // Get token from localStorage for Bearer auth
      const token = localStorage.getItem('auth_token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        
        // ============================================
        // RUNTIME IDENTITY MISMATCH DETECTION (P0 SECURITY)
        // ============================================
        // If /api/auth/me returns a DIFFERENT email than last known,
        // something has changed - force hard reset to prevent mixed UI/data
        const { checkIdentityMismatch, hardAuthReset, getHomeRouteForRole } = require('./utils/authSignal');
        if (checkIdentityMismatch(data)) {
          console.log('[useAuth] ⚠️ Identity mismatch! Forcing hard reset...');
          hardAuthReset();
          const targetRoute = getHomeRouteForRole(data.role);
          window.location.replace(targetRoute);
          return null; // Prevent further processing
        }
        
        setUser(data);
        setTenant(data.tenant);
        return data;
      } else {
        setUser(null);
        setTenant(null);
        return null;
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setUser(null);
      setTenant(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    // Broadcast logout to other tabs BEFORE the actual logout
    const { bumpAuthVersion, hardAuthReset } = require('./utils/authSignal');
    bumpAuthVersion('logout', null);
    
    // Clear all cached data
    hardAuthReset();
    
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.error("Logout error:", e);
    }
    setUser(null);
    setTenant(null);
    window.location.href = "/";
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return { user, setUser, tenant, setTenant, loading, checkAuth, logout, API };
};

// Loading Component
const LoadingSpinner = () => (
  <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-8">
    <div className="animate-pulse">
      <img
        src="https://customer-assets.emergentagent.com/job_tradespro-1/artifacts/b15t91h1_Free-Service-Pro-Logo.png"
        alt="Loading..."
        className="max-w-sm w-full h-auto object-contain opacity-50"
      />
    </div>
  </div>
);

// API Helper - uses Bearer token from localStorage as primary auth method
// This is critical for admin auth which can fail with cookie-only auth in preview environments
const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    ...options.headers,
  };
  
  // Always include Bearer token if available - this is the primary auth for admin
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Also send cookies as fallback
  });
};

// Admin-specific auth fetch with retry logic
// Tries cookie auth first, then Bearer token fallback
const adminAuthFetch = async (url, options = {}) => {
  const token = localStorage.getItem('auth_token');
  
  // First attempt: cookie auth with Bearer token attached
  const headers = {
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });
    
    // If successful or not auth error, return as-is
    if (response.ok || (response.status !== 401 && response.status !== 403)) {
      return response;
    }
    
    // Auth failed - if we have a token, try again with ONLY Bearer token
    // This handles cases where cookie is blocked but Bearer token is valid
    if (token && response.status === 401) {
      console.log('[Admin Auth] Cookie auth failed, retrying with Bearer token only');
      const retryResponse = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${token}`,
        },
        // Don't include credentials on retry - pure Bearer auth
      });
      return retryResponse;
    }
    
    return response;
  } catch (error) {
    console.error('[Admin Auth] Fetch error:', error);
    throw error;
  }
};

// Protected Route Component for Tenant Users
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [emailVerified, setEmailVerified] = useState(null);
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authFetch(`${API}/auth/me`);
        
        if (response.ok) {
          const data = await response.json();
          // Redirect platform admins to admin dashboard
          // Recognize multiple admin role patterns
          const role = data.role?.toLowerCase?.() || "";
          const isAdmin = data.is_admin === true;
          const hasAdminRole = (data.roles || []).some(r => 
            r?.toLowerCase?.() === "platform_admin" || 
            r?.toLowerCase?.() === "administrator"
          );
          const isPlatformAdmin = (
            (role === "platform_admin" || role === "administrator" || isAdmin || hasAdminRole) &&
            (data.tenant_id === null || data.tenant_id === undefined)
          );
          
          if (isPlatformAdmin) {
            window.location.href = "/admin";
            return;
          }
          setUser(data);
          setTenant(data.tenant);
          setIsAuthenticated(true);
          // Check email verification - default to true for legacy users
          setEmailVerified(data.email_verified !== false);
        } else {
          // Clear invalid token
          localStorage.removeItem('auth_token');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        localStorage.removeItem('auth_token');
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  if (isAuthenticated === null) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Suppress email-verification redirects ONLY while a first-value onboarding
  // session is active. Two signals are checked (either is sufficient):
  //   1) tenant.first_value_onboarding.active === true  (DB-backed truth, set
  //      during FVO seed + cleared once flow completes). Handles the initial
  //      mount race before the FVO context loads.
  //   2) sessionStorage flag 'fvo_session_in_progress'  (set by FVO context
  //      while active, persists through the immediate post-FVO continuation
  //      flow on /customers; cleared on logout via hardAuthReset()).
  // Once both signals clear, normal email-verification rules apply globally.
  const fvoActiveInDb = tenant?.first_value_onboarding?.active === true;
  const fvoSessionActive = (() => {
    try { return sessionStorage.getItem('fvo_session_in_progress') === '1'; }
    catch (_) { return false; }
  })();

  // Keep the sessionStorage flag in sync if FVO is active in DB but the flag
  // wasn't yet set (e.g., first render after signup, before FVO context fires).
  if (fvoActiveInDb && !fvoSessionActive) {
    try { sessionStorage.setItem('fvo_session_in_progress', '1'); } catch (_) {}
  }

  // Redirect unverified users to verify-email page (unless in active FVO session)
  if (emailVerified === false && !fvoActiveInDb && !fvoSessionActive) {
    return <Navigate to="/verify-email" replace />;
  }

  return <AppLayout user={user} tenant={tenant}>{children}</AppLayout>;
};

// Protected Route Component for Platform Admins
const AdminProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [authDebug, setAuthDebug] = useState({ 
    token_source: 'checking', 
    auth_status: null, 
    fail_reason: null,
    cookie_present: false,
    localStorage_token_present: false,
    me_status: null
  });
  const location = useLocation();

  // Helper: Check if user has platform admin privileges
  const isPlatformAdmin = (userData) => {
    if (!userData) return false;
    
    // Must have null tenant_id (platform-level, not tenant-bound)
    if (userData.tenant_id !== null && userData.tenant_id !== undefined) {
      return false;
    }
    
    // Check multiple admin indicators
    const role = userData.role?.toLowerCase?.() || "";
    const isAdmin = userData.is_admin === true;
    const roles = userData.roles || [];
    const hasAdminRole = roles.some(r => 
      r?.toLowerCase?.() === "platform_admin" || 
      r?.toLowerCase?.() === "administrator" ||
      r?.toLowerCase?.() === "admin"
    );
    
    return (
      role === "platform_admin" ||
      role === "administrator" ||
      isAdmin ||
      hasAdminRole
    );
  };

  useEffect(() => {
    const checkAuth = async () => {
      // Gather debug info
      const cookiePresent = document.cookie.includes('session_token');
      const localStorageToken = localStorage.getItem('auth_token');
      const localStorageTokenPresent = !!localStorageToken;
      
      setAuthDebug(prev => ({
        ...prev,
        cookie_present: cookiePresent,
        localStorage_token_present: localStorageTokenPresent,
      }));

      // Check if user was passed via navigation state (fresh login)
      if (location.state?.user && isPlatformAdmin(location.state.user)) {
        setUser(location.state.user);
        setIsAuthenticated(true);
        setAuthDebug(prev => ({ 
          ...prev, 
          token_source: 'navigation_state', 
          auth_status: 'PASS', 
          fail_reason: null,
          me_status: 'skipped_fresh_login'
        }));
        return;
      }

      try {
        // Use admin-specific auth fetch with retry logic
        const response = await adminAuthFetch(`${API}/auth/me`);
        
        setAuthDebug(prev => ({ ...prev, me_status: response.status }));
        
        // Also call whoami for debug info (non-blocking)
        adminAuthFetch(`${API}/auth/whoami`).then(async (debugResponse) => {
          if (debugResponse.ok) {
            const debugData = await debugResponse.json();
            setAuthDebug(prev => ({
              ...prev,
              token_source: debugData.token_source,
              token_attached: debugData.token_attached,
            }));
          }
        }).catch(() => {});
        
        if (response.ok) {
          const data = await response.json();
          
          if (!isPlatformAdmin(data)) {
            console.log('[Admin Auth] User is not platform admin, redirecting to dashboard', {
              role: data.role,
              tenant_id: data.tenant_id,
              is_admin: data.is_admin,
              roles: data.roles
            });
            window.location.href = "/dashboard";
            return;
          }
          
          setUser(data);
          setIsAuthenticated(true);
          setAuthDebug(prev => ({ 
            ...prev, 
            auth_status: 'PASS', 
            fail_reason: null 
          }));
        } else {
          const errorText = await response.text().catch(() => 'unknown');
          console.log('[Admin Auth] Auth check failed:', response.status, errorText);
          
          setAuthDebug(prev => ({ 
            ...prev, 
            auth_status: 'FAIL', 
            fail_reason: `${response.status}: ${errorText.substring(0, 100)}`
          }));
          
          // Clear invalid token
          localStorage.removeItem('auth_token');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("[Admin Auth] Auth check exception:", error);
        setAuthDebug(prev => ({ 
          ...prev, 
          token_source: 'none', 
          auth_status: 'FAIL', 
          fail_reason: `exception: ${error.message}` 
        }));
        localStorage.removeItem('auth_token');
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, [location.state]);

  if (isAuthenticated === null) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    // Show debug banner in preview environment only
    const isPreview = window.location.hostname.includes('preview.emergentagent.com');
    return (
      <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center p-8">
        {isPreview && (
          <div className="bg-red-900/50 border border-red-500 rounded-lg p-6 max-w-lg w-full mb-4">
            <h2 className="text-red-400 font-bold text-lg mb-2">🔐 Admin Auth Debug (Preview Only)</h2>
            <div className="text-white text-sm space-y-1 font-mono">
              <p><strong>Cookie present:</strong> <span className={authDebug.cookie_present ? 'text-green-400' : 'text-red-400'}>{authDebug.cookie_present ? 'yes' : 'no'}</span></p>
              <p><strong>localStorage token:</strong> <span className={authDebug.localStorage_token_present ? 'text-green-400' : 'text-red-400'}>{authDebug.localStorage_token_present ? 'yes' : 'no'}</span></p>
              <p><strong>Token source:</strong> {authDebug.token_source || 'none'}</p>
              <p><strong>/me status:</strong> <span className={authDebug.me_status === 200 ? 'text-green-400' : 'text-red-400'}>{authDebug.me_status || 'pending'}</span></p>
              <p><strong>Auth status:</strong> <span className={authDebug.auth_status === 'PASS' ? 'text-green-400' : 'text-red-400'}>{authDebug.auth_status || 'unknown'}</span></p>
              <p><strong>Fail reason:</strong> <span className="text-yellow-400">{authDebug.fail_reason || 'none'}</span></p>
            </div>
            <div className="mt-4 pt-4 border-t border-red-500/50">
              <p className="text-xs text-zinc-400">If you just logged in and see this, try refreshing the page. If persists, check browser cookie settings.</p>
            </div>
          </div>
        )}
        <Navigate to="/" replace />
      </div>
    );
  }

  return <AdminLayout user={user}>{children}</AdminLayout>;
};

// Translation hook
import { TranslationProvider, useTranslationStatus } from "./hooks/useTranslation";

// Protected Route Component for Technicians
const TechProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [technician, setTechnician] = useState(null);
  const [translationEntitled, setTranslationEntitled] = useState(false);

  useEffect(() => {
    // Fetch technician profile to get preferred_language
    const fetchTechnicianProfile = async () => {
      try {
        const response = await authFetch(`${API}/tech/me`);
        if (response.ok) {
          const data = await response.json();
          const techData = data.technician;
          setTechnician(techData);
          
          // Check if tenant is entitled to Spanish translation
          if (techData?.preferred_language === "es") {
            const statusRes = await authFetch(`${API}/translate/status`);
            if (statusRes.ok) {
              const statusData = await statusRes.json();
              setTranslationEntitled(statusData.entitled);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch technician profile:", error);
      }
    };

    const checkAuth = async () => {
      try {
        const response = await authFetch(`${API}/auth/me`);
        
        if (response.ok) {
          const data = await response.json();
          // Allow technicians and owners/admins to access tech portal
          if (!["technician", "tenant_owner", "admin"].includes(data.role)) {
            window.location.href = "/dashboard";
            return;
          }
          setUser(data);
          setTenant(data.tenant);
          setIsAuthenticated(true);
          // Fetch technician profile for translation
          fetchTechnicianProfile();
        } else {
          localStorage.removeItem('auth_token');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        localStorage.removeItem('auth_token');
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  if (isAuthenticated === null) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <TranslationProvider technician={technician} translationEntitled={translationEntitled}>
      <TechLayout user={user} tenant={tenant}>{children}</TechLayout>
    </TranslationProvider>
  );
};

// Main Router Component
function AppRouter() {
  const location = useLocation();

  // =========================================================================
  // P0 HOTFIX: Only route session_id URLs to legacy AuthCallback if Emergent Auth is enabled.
  // Previously, ANY URL with session_id= in the hash would route to the vulnerable
  // Emergent OAuth callback, even when Supabase Auth was configured.
  // =========================================================================
  const USE_EMERGENT_AUTH = process.env.REACT_APP_USE_EMERGENT_AUTH === 'true';
  
  if (USE_EMERGENT_AUTH && location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // If Supabase auth is active and session_id appears (shouldn't happen), ignore it
  // and let normal routing handle the request.

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/setup" element={<SetupPage />} />
      
      {/* Auth Callbacks */}
      <Route path="/auth/callback" element={<SupabaseAuthCallback />} />
      
      {/* Protected Tenant Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/customers/:customerId" element={<ProtectedRoute><CustomerDetail /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
      <Route path="/jobs/:jobId" element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
      <Route path="/follow-ups" element={<ProtectedRoute><FollowUps /></ProtectedRoute>} />
      <Route path="/estimates" element={<ProtectedRoute><Estimates /></ProtectedRoute>} />
      {/* Redirect old catalog route to Settings > Catalog */}
      <Route path="/estimates/catalog" element={<Navigate to="/settings?tab=catalog" replace />} />
      <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      
      {/* Reports */}
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/reports/money" element={<ProtectedRoute><ReportsMoney /></ProtectedRoute>} />
      <Route path="/reports/work" element={<ProtectedRoute><ReportsWork /></ProtectedRoute>} />
      <Route path="/reports/customers" element={<ProtectedRoute><ReportsCustomers /></ProtectedRoute>} />
      
      <Route path="/technicians" element={<ProtectedRoute><Technicians /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/settings/upgrades" element={<ProtectedRoute><Upgrades /></ProtectedRoute>} />
      <Route path="/settings/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/settings/subscription" element={<ProtectedRoute><SubscriptionManagement /></ProtectedRoute>} />
      <Route path="/settings/billing-analytics" element={<ProtectedRoute><BillingAnalytics /></ProtectedRoute>} />
      <Route path="/settings/reviews" element={<ProtectedRoute><ReviewSettings /></ProtectedRoute>} />
      <Route path="/settings/review-analytics" element={<ProtectedRoute><ReviewAnalytics /></ProtectedRoute>} />
      <Route path="/settings/booking" element={<ProtectedRoute><BookingSettings /></ProtectedRoute>} />
      <Route path="/settings/reminders" element={<ProtectedRoute><ReminderSettings /></ProtectedRoute>} />
      <Route path="/settings/email-marketing" element={<ProtectedRoute><EmailMarketing /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
      <Route path="/ideas" element={<ProtectedRoute><Ideas /></ProtectedRoute>} />
      
      {/* Public Review Landing Page */}
      <Route path="/review/:token" element={<ReviewLanding />} />
      
      {/* Public Booking Page */}
      <Route path="/book/:tenantId" element={<PublicBookingPage />} />
      
      {/* Public Unsubscribe Page */}
      <Route path="/unsubscribe/:tenantId/:customerId" element={<UnsubscribePage />} />
      
      {/* Public Estimate Page */}
      <Route path="/public/estimate/:publicToken" element={<PublicEstimate />} />
      <Route path="/public/invoice/:publicToken" element={<PublicInvoice />} />
      
      {/* TrustCommerce Return Bridge (for popup) */}
      <Route path="/billing/trustcommerce/return" element={<TrustCommerceReturnPage />} />
      
      {/* Technician Activation & Password Reset (Public) */}
      <Route path="/activate" element={<ActivatePage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      
      {/* Platform Admin Routes */}
      <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
      <Route path="/admin/tenants" element={<AdminProtectedRoute><AdminTenants /></AdminProtectedRoute>} />
      <Route path="/admin/tenants/lifecycle" element={<AdminProtectedRoute><AdminTenantLifecycle /></AdminProtectedRoute>} />
      <Route path="/admin/launch-cleanup" element={<AdminProtectedRoute><AdminLaunchCleanup /></AdminProtectedRoute>} />
      <Route path="/admin/tickets" element={<AdminProtectedRoute><AdminTickets /></AdminProtectedRoute>} />
      <Route path="/admin/ideas" element={<AdminProtectedRoute><AdminIdeas /></AdminProtectedRoute>} />
      <Route path="/admin/features" element={<AdminProtectedRoute><AdminFeatures /></AdminProtectedRoute>} />
      <Route path="/admin/bundles" element={<AdminProtectedRoute><AdminBundles /></AdminProtectedRoute>} />
      <Route path="/admin/subscriptions" element={<AdminProtectedRoute><AdminSubscriptions /></AdminProtectedRoute>} />
      <Route path="/admin/admins" element={<AdminProtectedRoute><AdminAdmins /></AdminProtectedRoute>} />
      <Route path="/admin/system" element={<AdminProtectedRoute><AdminSystemUtilities /></AdminProtectedRoute>} />
      <Route path="/admin/system/health" element={<AdminProtectedRoute><AdminSystemHealth /></AdminProtectedRoute>} />
      <Route path="/admin/billing" element={<AdminProtectedRoute><AdminBillingLogs /></AdminProtectedRoute>} />
      <Route path="/admin/service-types" element={<AdminProtectedRoute><AdminServiceTypes /></AdminProtectedRoute>} />
      <Route path="/admin/tenant-communications" element={<AdminProtectedRoute><AdminTenantComms /></AdminProtectedRoute>} />
      <Route path="/admin/tenant-communications/templates/new" element={<AdminProtectedRoute><TemplateEditor /></AdminProtectedRoute>} />
      <Route path="/admin/tenant-communications/templates/:templateId" element={<AdminProtectedRoute><TemplateEditor /></AdminProtectedRoute>} />
      
      {/* Tech Portal Routes */}
      <Route path="/tech" element={<TechProtectedRoute><TechDashboard /></TechProtectedRoute>} />
      <Route path="/tech/visits" element={<TechProtectedRoute><TechVisits /></TechProtectedRoute>} />
      <Route path="/tech/visits/:visitId" element={<TechProtectedRoute><TechVisitDetail /></TechProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Apply iOS viewport height fix globally
  useIOSViewportFix();
  
  // ============================================
  // CROSS-TAB AUTH SIGNAL LISTENER (P0 SECURITY + UX)
  // ============================================
  // CRITICAL: Prevents stale contractor DATA from displaying after auth changes.
  // When auth changes in another tab:
  // 1. Purge ALL cached data (auth + domain data like customers/estimates/etc)
  // 2. Redirect to correct shell based on new identity
  useEffect(() => {
    const { 
      initAuthSignal, 
      subscribeToAuthChanges, 
      handleCrossTabAuthChange 
    } = require('./utils/authSignal');
    
    // Initialize auth signal system
    initAuthSignal();
    
    const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
    
    const unsubscribe = subscribeToAuthChanges((payload) => {
      console.log('[App] ⚠️ Auth change detected from another tab:', payload.reason || payload.event);
      
      // CRITICAL: Do NOT just reload - must purge cached domain data first
      // Then redirect to correct shell based on new identity
      handleCrossTabAuthChange(API_URL);
    });
    
    return unsubscribe;
  }, []);
  
  // Add iOS class to html element for iOS-specific CSS and initialize sticky offset fix
  useEffect(() => {
    const isIOSDevice = /iPhone|iPad|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    if (isIOSDevice) {
      document.documentElement.classList.add('ios');
      document.documentElement.classList.add('ios-scroll-fix');
      
      // Initialize iOS sticky offset fix
      const { initIOSStickyOffsetFix } = require('./utils/iosStickyOffsetFix');
      const cleanup = initIOSStickyOffsetFix();
      
      return () => {
        cleanup?.();
        document.documentElement.classList.remove('ios');
        document.documentElement.classList.remove('ios-scroll-fix');
      };
    }
    
    return () => {
      document.documentElement.classList.remove('ios');
      document.documentElement.classList.remove('ios-scroll-fix');
    };
  }, []);
  
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <EntitlementProvider>
            <FirstValueOnboardingProvider>
              <SupportSessionBanner />
              <AppRouter />
              <Toaster position="top-right" richColors />
            </FirstValueOnboardingProvider>
          </EntitlementProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
